/**
 * Keeps Plyr's settings menu (quality / speed) inside the player box.
 *
 * Plyr opens the menu upward from the gear button, and `.plyr--video` clips with
 * `overflow: hidden` — so on any player shorter than the open pane the options
 * sit in the DOM with zero visible pixels (SM-9058: the speed list rendered
 * below the player's bottom edge and the viewer could only click "Go back").
 *
 * CSS cannot express "no taller than the player" on its own here: the menu's
 * containing block is the gear button, not the player, and a container query
 * would need size containment on a wrapper whose height is not always definite
 * (the compact variant drops the aspect ratio and inherits height).
 *
 * So the space above the control bar is measured per player and published as
 * `--ms-plyr-menu-max`, which `_components.video-player.scss` caps the panes to.
 * A ResizeObserver covers what a one-shot measurement misses: entering
 * fullscreen, device rotation, responsive reflow, lazily-mounted feed cards.
 */

const MENU_VAR = '--ms-plyr-menu-max'

// Plyr's own 10px gap between menu and control bar, plus breathing room at the
// player's top edge so the pane never looks flush against it.
const MENU_GUTTER = 18

// Below this a cap is worse than the overflow it prevents — one row and a
// scrollbar is unusable, so let short players bleed instead.
const MIN_MENU_HEIGHT = 72

const RESCAN_DELAY_MS = 200

let resizeObserver = null
let mutationObserver = null
let rescanTimer = null

// ResizeObserver keeps a strong reference to everything it observes, so players
// that Inertia has torn down are released here rather than held for the tab's
// lifetime.
const observed = new Set()

function applyCap(root) {
    if (!root.isConnected) return

    const controlsHeight = root.querySelector('.plyr__controls')?.offsetHeight ?? 0
    const available = root.clientHeight - controlsHeight - MENU_GUTTER

    root.style.setProperty(MENU_VAR, `${Math.max(MIN_MENU_HEIGHT, Math.round(available))}px`)
}

function scan() {
    observed.forEach((root) => {
        if (root.isConnected) return

        resizeObserver.unobserve(root)
        observed.delete(root)
    })

    document.querySelectorAll('.plyr--video').forEach((root) => {
        applyCap(root)

        if (observed.has(root)) return

        resizeObserver.observe(root)
        observed.add(root)
    })
}

function scheduleScan() {
    if (rescanTimer) return

    rescanTimer = window.setTimeout(() => {
        rescanTimer = null
        scan()
    }, RESCAN_DELAY_MS)
}

/**
 * Idempotent — safe to call more than once (Inertia re-mounts, HMR).
 */
export function initPlyrMenuFit() {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return
    if (resizeObserver) return

    resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => applyCap(entry.target))
    })

    // Plyr builds its wrapper imperatively, so players appear without any Vue
    // lifecycle hook to hang off. A debounced rescan on DOM mutation catches
    // every instance regardless of which component created it.
    mutationObserver = new MutationObserver(scheduleScan)
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    // Fullscreen swaps the player's box without necessarily reflowing anything
    // the observers watch.
    document.addEventListener('fullscreenchange', scheduleScan)
    document.addEventListener('webkitfullscreenchange', scheduleScan)

    scan()
}
